import Hashids from 'hashids'
import { Model } from '../models'
import { watch, onUnmounted, Ref, WatchSource } from 'vue'
import { Toast } from '../models/Toast'
import { store } from '@/store'
import { useI18n } from 'vue-i18n'
import i18n from '@/i18n'

const t = i18n.global.t


// Short hand for obtaining enviromental values
export const env = (key: string): string => {
  return process.env['VUE_APP_' + key] || ''
}

// Update nested object using path
export const updateObject = (object: any, path: string, value: any) => {
  const stack = path.split('/')
  while(stack.length > 1){
    object = object[stack.shift() as string]
  }
  object[stack.shift() as string] = value
}

// Apply model to object
export const applyModel = <T>(object: any, model: Model<T>) => {  
  return Object.assign({...new model()}, object) as T
}
// Remove model from object
export const cleanObject = <T>(object: T): T => {
  return JSON.parse(JSON.stringify(object)) as T
}


// Search object and return results
export const searchObject = (object: object, query: string): object => {
  const results = object
  return results
}


// Sort array by key
export const sortArray = <T>(array: any[], key: keyof T): T[] => {
  return array.sort((a, b) => a[key] - b[key])
}

// Sort object by key and return sorted array
export const sortObject = <T>(object: object, key: keyof T): T[] => {
  return sortArray(Object.values(object), key)
}

// Round numbers
export const round = (value: number, decimals: number): number => {
  const multiplier = Math.pow(10, decimals || 0)
  return Math.round(value * multiplier) / multiplier
}

// Get the current unix timestamp (seconds)
export const timestamp = (): number => {
  return round(new Date().getTime() / 1000, 0)
}

// Convert second timestamp to clock string
export const getFormattedTime = (unix: number): string => {
  const date = new Date(unix * 1000)
  return date.getHours().toString().padStart(2,'0') + ':' + ('0' + date.getMinutes()).substr(-2)
}

// Get formatted duration (10t 2s / 10 min / 23 sec)
export const getFormattedDuration = (seconds: number) => {
  seconds = Math.abs(seconds)
  const h = Math.floor(seconds / 60 / 60),
    m = Math.floor((seconds - (h * 60 * 60)) / 60),
    s = Math.floor((seconds - (h * 60 * 60) - (m * 60)))
  
  return h == 0 && m == 0 && s == 0 ? t('clock.minutes_short', [0]) :
    h == 0 && m == 0 ? t('clock.one_minute_short') :
      h == 0 ? t('clock.minutes_short', [m]) :
        m == 0 ? t('clock.hours_short', [h]) : t('clock.hours_minutes_short', [h,m])   
}

// Get count-down timer from seconds (60s => 1:00)
export const getFormattedCountdown = (seconds: number) => {
  const date = new Date(0)
  date.setSeconds(Math.abs(seconds))
  const timeString = date.toISOString().substr(11, 8)
  return (seconds < 0 ? '+':'') + timeString.replace(/^[0:]+/, '')
}

// Get formatted date
export const getFormattedDate = (seconds: number): string => {
  const date = new Date(seconds * 1000)
  const dd = String(date.getDate()).padStart(2, '0')
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const yyyy = date.getFullYear()
  return mm + '/' + dd + '/' + yyyy
}

// Compare two objects and return an object with changed properties
export const compareObjects = <T>(obj1: {[key: string]: any}, obj2: {[key: string]: any}): Partial<T> => {
  // Author: https://codereview.stackexchange.com/a/11580  
  if (!obj1 || !obj2) return {} as Partial<T>
  
  let kDiff: any
  const diff: any = {}
  for (const k in obj1) {
    if (!obj1.hasOwnProperty(k)) {
      // do nuffin
    } else if (typeof obj1[k] != 'object' || typeof obj2[k] != 'object') {
      if (!(k in obj2) || obj1[k] !== obj2[k]) {
        diff[k] = obj2[k]
      }
    } else if (kDiff = compareObjects<any>(obj1[k], obj2[k])) {
      if (Object.keys(kDiff).length) {
        diff[k] = kDiff
      }
    }
  }
  for (const k in obj2) {
    if (obj2.hasOwnProperty(k) && obj1 && !(k in obj1)) {
      diff[k] = obj2[k]
    }
  }
  for (const k in diff) {
    if (diff.hasOwnProperty(k)) {
      return diff
    }
  }
  return {} as Partial<T>
}

// Debounce function
export const debounce = (cb: () => any, delay: number) => {
  let timer: NodeJS.Timeout     
  return () => {
    clearTimeout(timer)
    timer = setTimeout(() => {
      cb()
    }, delay)
  }
}

// Debounce Vue Ref
export const debounceRef = <T>(ref: WatchSource<T>, cb: (newVal: T, oldVal: T) => any, delay: number) => {
  let timer: NodeJS.Timeout
  const stop = watch(ref, (newVal, oldVal) => {
    clearTimeout(timer)
    timer = setTimeout(() => {
      cb(newVal, oldVal)
    }, delay)
  }, { immediate: false, deep: false })
  onUnmounted(() => {
    stop()
  })
}

// Show a toast on the screen
export const toast = (toast: Toast): void => {
  store.global.toast(toast)
}

// Get device info
export const platform = () => {
  const platform = navigator.platform
  return {
    isApple: /(Mac|iPhone|iPod|iPad)/i.test(platform),
    isMac: /(Mac)/i.test(platform),
    isIOS: /(iPhone|iPod|iPad)/i.test(platform),
    isWindows: /(Win)/i.test(platform)
  }
}

// Copy to clipboard
export const copyToClipboard = (str: string) => {
  const el = document.createElement('textarea')
  el.value = str
  document.body.appendChild(el)
  el.select()
  document.execCommand('copy')
  document.body.removeChild(el)
  toast({title: t('toasts.copied.title') as string, msg: t('toasts.copied.desc', [str]) as string, type: 'success'})
}

// Set window title
export const setWindowTitle = (title: string) => {
  document.title = title + ' - tripply.dk'
}